import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/client/components/ui/alert-dialog';
import { Button } from '@/client/components/ui/button';
import { PeopleList } from '@/client/components/people/PeopleList';
import { PersonDetail } from '@/client/components/people/PersonDetail';
import { PersonDialog } from '@/client/components/people/PersonDialog';
import type { PersonFormState } from '@/client/components/people/PersonDialog';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { trpcVanilla } from '@/client/utils/trpc-client';

type Employee = RouterOutputs['employees']['list'][number];

const searchSchema = z.object({
  id: z.string().uuid().optional(),
  tab: z.enum(['overview', 'projects', 'time']).optional().default('overview'),
});

export const Route = createFileRoute('/_app/people')({
  beforeLoad: async () => {
    const me = await trpcVanilla.user.me.query();
    if (me.role !== 'ADMIN') throw redirect({ to: '/' });
  },
  validateSearch: (search: Record<string, unknown>) => searchSchema.parse(search),
  component: PeoplePage,
});

function PeoplePage() {
  const navigate = useNavigate();
  const { id: selectedId, tab: activeTab } = Route.useSearch();

  const utils = trpc.useUtils();
  const { data: people = [], isLoading } = trpc.employees.list.useQuery();

  const effectiveId = selectedId ?? people[0]?.id;
  const selectedPerson = people.find((p) => p.id === effectiveId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPerson, setDialogPerson] = useState<Employee | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = trpc.employees.create.useMutation({
    onSuccess: () => {
      utils.employees.list.invalidate();
      setDialogOpen(false);
    },
    onError: (e) => setFormError(e.message),
  });

  const updateMutation = trpc.employees.update.useMutation({
    onSuccess: () => {
      utils.employees.list.invalidate();
      setDialogOpen(false);
    },
    onError: (e) => setFormError(e.message),
  });

  const removeMutation = trpc.employees.remove.useMutation({
    onSuccess: (_, vars) => {
      utils.employees.list.invalidate();
      setDeleteTarget(null);
      if (vars.id === effectiveId) {
        navigate({ to: '/people', search: { tab: activeTab } });
      }
    },
  });

  function selectPerson(id: string) {
    navigate({ to: '/people', search: { id, tab: activeTab } });
  }

  function changeTab(tab: string) {
    navigate({
      to: '/people',
      search: { id: effectiveId, tab: tab as 'overview' | 'projects' | 'time' },
    });
  }

  function openAdd() {
    setDialogPerson(null);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(person: Employee) {
    setDialogPerson(person);
    setFormError(null);
    setDialogOpen(true);
  }

  function handleSubmit(form: PersonFormState) {
    setFormError(null);
    if (dialogPerson) {
      updateMutation.mutate({
        id: dialogPerson.id,
        full_name: form.full_name || undefined,
        job_title: form.job_title || undefined,
        rate: form.rate ? Number(form.rate) : undefined,
        rate_type: form.rate_type || undefined,
      });
    } else {
      createMutation.mutate({
        email: form.email,
        full_name: form.full_name,
        job_title: form.job_title || undefined,
        rate: form.rate ? Number(form.rate) : undefined,
        rate_type: form.rate_type || undefined,
      });
    }
  }

  return (
    <div className="-m-6 flex h-[calc(100%+3rem)] overflow-hidden">
      <PeopleList
        people={people}
        isLoading={isLoading}
        selectedId={effectiveId}
        onSelect={selectPerson}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex h-14 shrink-0 items-center justify-between border-b px-6">
          <h2 className="text-base font-semibold">People</h2>
          <Button size="sm" onClick={openAdd}>
            Invite
          </Button>
        </div>

        {!isLoading && !selectedPerson && (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">No people yet.</p>
              <Button className="mt-3" size="sm" onClick={openAdd}>
                Invite your first person
              </Button>
            </div>
          </div>
        )}

        {selectedPerson && (
          <div className="flex-1 overflow-y-auto p-8">
            <PersonDetail
              person={selectedPerson}
              activeTab={activeTab}
              onTabChange={changeTab}
              onEdit={() => openEdit(selectedPerson)}
              onDelete={() => setDeleteTarget(selectedPerson)}
            />
          </div>
        )}
      </div>

      <PersonDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        person={dialogPerson}
        onSubmit={handleSubmit}
        isPending={dialogPerson ? updateMutation.isPending : createMutation.isPending}
        error={formError}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteTarget?.full_name ?? deleteTarget?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate their account and prevent them from logging in. Their data (timesheets, invoices) will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={removeMutation.isPending}
              onClick={() => deleteTarget && removeMutation.mutate({ id: deleteTarget.id })}
            >
              {removeMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
